import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBlogs } from "../../../../api/blogs";
import { isBlogCoverFallback, resolveBlogCoverSrc } from "../../../../utils/blogImages";
import { resolveBlogPdfUrl, blogPdfFileName } from "../../../../utils/blogPdf";
import { sanitizeBlogHtml } from "../../../../utils/blogHtml";
import { readingTimeLabelFromBlog } from "../../../../utils/readingTime";
import { isBlogPublished } from "../../../../utils/blogStatus";
import SEO from "../../../../utils/SEO";
import { getLanguageMeta } from "../../../../i18n/languages";
import "./PublicBlogDetail.css";
import { FaArrowLeft } from "react-icons/fa";

const PublicBlogDetail = () => {
    const { t, i18n } = useTranslation();
    const tp = (k, d) => t(`pages.blog.${k}`, d);

    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const fetchBlog = async () => {
            try {
                const data = await getBlogs();
                if (cancelled) return;

                const list = Array.isArray(data) ? data : [];
                const target = list.find((item) => item?.slug === slug || item?._id === slug);

                if (!target || !isBlogPublished(target)) {
                    setError(tp('blogNotFoundDesc', "The requested post does not exist or may have been removed."));
                } else {
                    setBlog(target);
                }
            } catch (err) {
                if (cancelled) return;
                const msg =
                    err?.response?.data?.message ||
                    err?.message ||
                    tp('errorLoading', "Failed to load blog posts. Please try again.");
                setError(msg);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchBlog();

        return () => {
            cancelled = true;
        };
    }, [slug]);

    const imageSrc = useMemo(() => resolveBlogCoverSrc(blog?.image), [blog?.image]);
    const coverIsFallback = useMemo(() => isBlogCoverFallback(blog?.image), [blog?.image]);

    const pdfSrc = useMemo(() => resolveBlogPdfUrl(blog?.pdfUrl), [blog?.pdfUrl]);

    const createdLabel = useMemo(() => {
        if (!blog?.createdAt) return "";
        const date = new Date(blog.createdAt);
        if (Number.isNaN(date.getTime())) return "";
        return date.toLocaleDateString(getLanguageMeta(i18n.language).htmlLang, {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    }, [blog?.createdAt, i18n.language]);

    const readingTime = useMemo(() => {
        if (!blog) return `1 ${tp('minutesRead', 'min read')}`;
        const label = readingTimeLabelFromBlog(blog);
        const num = parseInt(label) || 1;
        return `${num} ${tp('minutesRead', 'min read')}`;
    }, [blog, tp]);

    const authorLabel = useMemo(() => {
        return String(blog?.author || "Offshore Advisory Editorial");
    }, [blog?.author]);

    const seoTitle = useMemo(() => {
        const custom = String(blog?.metaTitle || "").trim();
        return custom || String(blog?.title || "Blog").trim();
    }, [blog?.metaTitle, blog?.title]);

    const seoDescription = useMemo(() => {
        const custom = String(blog?.metaDescription || "").trim();
        return custom || String(blog?.intro || "").trim();
    }, [blog?.metaDescription, blog?.intro]);

    const seoKeywords = useMemo(() => String(blog?.metaKeywords || "").trim(), [blog?.metaKeywords]);

    const seoUrl = useMemo(() => {
        const slugValue = blog?.slug || slug;
        return slugValue ? `/blogs/${slugValue}` : "/blogs";
    }, [blog?.slug, slug]);

    if (loading) {
        return (
            <main className='main'>
                <div className="section pb-section container">
                    <section className="pb" aria-label="Blog details loading">
                        <div className="pb-topbar-skeleton" />
                        <div className="pb-hero-skeleton" />
                        <div className="pb-panel-skeleton" />
                    </section>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className='main'>
                <div className="section pb-section container">
                    <section className="pb" aria-label="Blog details error">
                        <div className="pb-state error" role="alert">
                            <h2 className="pb-state-title">{tp('couldNotLoadBlog', "Could not load this blog")}</h2>
                            <p className="pb-state-text">{error}</p>
                            <Link className="pb-link-back" to="/blogs"><FaArrowLeft /> {tp('backToList', "Back to Blog List")}</Link>
                        </div>
                    </section>
                </div>
            </main>
        );
    }

    if (!blog) {
        return (
            <main className='main'>
                <div className="section pb-section container">
                    <section className="pb" aria-label="Blog details empty">
                        <div className="pb-state">
                            <h2 className="pb-state-title">{tp('blogNotFound', "Blog not found")}</h2>
                            <p className="pb-state-text">{tp('blogNotFoundDesc', "The requested post does not exist or may have been removed.")}</p>
                            <Link className="pb-link-back" to="/blogs">{tp('backToList', "Back to Blog List")}</Link>
                        </div>
                    </section>
                </div>
            </main>

        );
    }

    return (
        <main className='main'>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={seoKeywords || undefined}
                author={authorLabel}
                url={seoUrl}
                type="article"
            />
            <div className="section pb-section container">
                <section className=" pb" aria-label="Blog details">
                    <div className="pb-topbar">
                        <Link to="/blogs" className="pb-link-back"><FaArrowLeft /> {tp('backToList', "Back to Blog List")}</Link>
                    </div>

                    <article className="pb-article">
                        <header className="pb-hero">
                            <img
                                className={`pb-hero-image${coverIsFallback ? " pb-hero-image--fallback" : ""}`}
                                src={imageSrc}
                                alt={coverIsFallback ? "Offshore Advisory" : (blog?.title || "Blog cover")}
                            />

                            <div className="pb-hero-overlay" />

                            <div className="pb-hero-content">
                                <h1 className="pb-title">{blog?.title || "Blog"}</h1>

                                <div className="pb-meta-row">
                                    <span className="pb-chip">{tp('by', 'By')} {authorLabel}</span>
                                    {createdLabel ? <span className="pb-chip">{createdLabel}</span> : null}
                                    <span className="pb-chip">{readingTime}</span>
                                </div>
                            </div>
                        </header>

                        {blog?.intro ? (
                            <section className="pb-intro" aria-label="Blog intro">
                                <p className="pb-intro-text">{blog.intro}</p>
                            </section>
                        ) : null}

                        {pdfSrc ? (
                            <section className="pb-pdf pb-pdf--name-only" aria-label="Attached PDF document">
                                <a
                                    className="pb-pdf-name-link"
                                    href={pdfSrc}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {blogPdfFileName(blog?.pdfUrl)}
                                </a>
                            </section>
                        ) : null}

                        <section className="pb-content-wrap" aria-label="Blog content">
                            <div
                                className="pb-content"
                                dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(blog?.content || "") }}
                            />
                        </section>
                    </article>
                </section>
            </div>
        </main>
    );
};

export default PublicBlogDetail;
